/**
 * @fileoverview Coordinate paginated staff tasks, task selection, and domain-specific review panels. The tasks API owns visibility, assignment, counts, and transitions; taskAdapters supplies presentation identity. Query changes and request cancellation keep stale results from replacing the active list or selected task.
 */
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../api';
import ToastContext from '../../ToastContext';
import AppIcon from '../shared/AppIcon';
import AppModal from '../shared/AppModal';
import OutreachWorkspace from '../Intake/OutreachWorkspace';
import { formatDate, getPipedriveRecordUrl } from '../Intake/intakeFormatters';
import {
  fetchIntakeDuplicateCandidates,
  mergeIntakeDuplicateCandidate,
  resolveIntakeDuplicateCandidate,
} from '../Intake/intakeApi';
import {
  taskActivityTitle,
  TASK_FAMILIES,
  TASK_TYPES,
  toInboxTask,
} from './taskAdapters';
import './TaskInbox.css';
import PaymentReviewPanel from './PaymentReviewPanel';
import PortalResult from './PortalResult';
import InboxLoading from './InboxLoading';
import TaskAssignment from './TaskAssignment';

const OWNERSHIP_VIEWS = [
  { key: 'mine', label: 'My work', icon: 'pi pi-user' },
  { key: 'unassigned', label: 'Needs assignment', icon: 'pi pi-user-plus' },
  { key: 'all', label: 'All work', icon: 'pi pi-inbox' },
];

function availableDefaultType(family) {
  return (TASK_TYPES.find((type) => (type.family || 'intake_document_coordination') === family) || TASK_TYPES[0]).key;
}

function humanizeKey(value) {
  return String(value || '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function ownerLabel(owner) {
  return owner?.name || owner?.email || 'Unassigned';
}

function leadSourceLabel(source) {
  if (!source?.key) return '';
  return [humanizeKey(source.key), humanizeKey(source.subtype)].filter(Boolean).join(' · ');
}

function InboxValue({ value, fallback = 'Not recorded' }) {
  return value || <span className="task-inbox__placeholder">{fallback}</span>;
}

function urgencyIcon(key) {
  if (key === 'overdue') return 'pi pi-exclamation-circle';
  if (key === 'due_today' || key === 'due_soon') return 'pi pi-clock';
  if (key === 'completed') return 'pi pi-check-circle';
  return 'pi pi-calendar';
}

const OUTREACH_WORKSPACE_TYPES = new Set(['request_documents', 'follow_up_documents']);
const UNRESOLVED_DUPLICATE_STATUSES = new Set(['likely_duplicate', 'possible_duplicate']);
const INBOX_VIEW_STATE_KEY = 'kc-internal:inbox:view-state:v1';

function duplicateCandidateLabel(candidate) {
  if (!candidate) return 'the selected lead';
  return candidate.prospect_name || candidate.lead_title || `Lead #${candidate.id}`;
}

const DUPLICATE_COMPARISON_FIELDS = [
  {
    key: 'name',
    label: 'Name',
    current: (task) => task.leadName,
    candidate: (lead) => duplicateCandidateLabel(lead),
    exactReasons: ['name_exact', 'name_key_exact'],
    similarReasons: ['name_near_exact'],
  },
  {
    key: 'email',
    label: 'Email',
    current: (task) => task.prospectEmail,
    candidate: (lead) => lead.prospect_email,
    exactReasons: ['email_exact'],
  },
  {
    key: 'phone',
    label: 'Phone',
    current: (task) => task.prospectPhone,
    candidate: (lead) => lead.prospect_phone,
    exactReasons: ['phone_exact'],
    normalize: (value) => String(value || '').replace(/\D/g, ''),
  },
  {
    key: 'organization',
    label: 'Organization',
    current: (task) => task.company,
    candidate: (lead) => lead.prospect_organization_name,
    exactReasons: ['organization_name_exact', 'pipedrive_organization_exact'],
  },
  {
    key: 'defendant',
    label: 'Defendant',
    current: (task) => task.defendant,
    candidate: (lead) => lead.defendant_name_raw || lead.defendant_name_normalized,
    exactReasons: ['defendant_exact'],
  },
  {
    key: 'claim_type',
    label: 'Claim type',
    current: (task) => humanizeKey(task.claimType),
    candidate: (lead) => humanizeKey(lead.category),
  },
  {
    key: 'state',
    label: 'Client state',
    current: (task) => task.state,
    candidate: (lead) => lead.state_client || lead.state_events || lead.state_defendant,
    exactReasons: ['state_client_exact'],
  },
  {
    key: 'stage',
    label: 'Intake stage',
    current: (task) => humanizeKey(task.stage),
    candidate: (lead) => humanizeKey(lead.current_stage),
  },
];

function normalizedComparisonValue(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');
}

function duplicateFieldMatch(field, currentValue, candidateValue, reasonCodes) {
  if ((field.similarReasons || []).some((reason) => reasonCodes.has(reason))) return 'similar';
  if ((field.exactReasons || []).some((reason) => reasonCodes.has(reason))) return 'identical';
  if (!currentValue || !candidateValue) return null;
  const normalize = field.normalize || normalizedComparisonValue;
  return normalize(currentValue) === normalize(candidateValue) ? 'identical' : null;
}

function DuplicateLeadComparison({ task, candidate }) {
  const matchedLead = candidate?.candidate_lead || {};
  const reasonCodes = new Set(candidate?.reason_codes || []);
  const rows = DUPLICATE_COMPARISON_FIELDS.map((field) => {
    const currentValue = field.current(task);
    const candidateValue = field.candidate(matchedLead);
    return {
      ...field,
      currentValue,
      candidateValue,
      match: duplicateFieldMatch(field, currentValue, candidateValue, reasonCodes),
    };
  });

  return (
    <div className="task-inbox__duplicate-comparison">
      <table>
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">
              <span>Current lead</span>
              <Link to={`/intake/leads/${task.leadId}?from=inbox`}>{task.leadName}</Link>
            </th>
            <th scope="col">
              <span>Potential duplicate</span>
              <Link to={`/intake/leads/${candidate.candidate_lead_id}?from=inbox`}>
                {duplicateCandidateLabel(matchedLead)}
              </Link>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr className={row.match ? `task-inbox__duplicate-match task-inbox__duplicate-match--${row.match}` : undefined} key={row.key}>
              <th scope="row">
                <span>{row.label}</span>
                {row.match ? <small>{row.match === 'identical' ? 'Identical' : 'Similar'}</small> : null}
              </th>
              <td><InboxValue value={row.currentValue} /></td>
              <td><InboxValue value={row.candidateValue} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InboxDuplicateResolution({ task, onTaskChanged }) {
  const showToast = useContext(ToastContext);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [clearingId, setClearingId] = useState(null);
  const [mergeCandidate, setMergeCandidate] = useState(null);
  const [mergeReason, setMergeReason] = useState('');
  const [mergeNotes, setMergeNotes] = useState('');
  const [merging, setMerging] = useState(false);
  const mountedRef = useRef(true);
  const candidateRequestRef = useRef(0);

  useEffect(() => () => {
    mountedRef.current = false;
    candidateRequestRef.current += 1;
  }, []);

  const loadCandidates = useCallback(async () => {
    const requestId = candidateRequestRef.current + 1;
    candidateRequestRef.current = requestId;
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetchIntakeDuplicateCandidates(task.leadId, { page_size: 100 });
      if (!mountedRef.current || requestId !== candidateRequestRef.current) return;
      setCandidates((response.data?.duplicate_candidates || []).filter((candidate) => (
        UNRESOLVED_DUPLICATE_STATUSES.has(candidate.status)
        && candidate.candidate_lead?.status !== 'duplicate'
      )));
    } catch (candidateError) {
      if (!mountedRef.current || requestId !== candidateRequestRef.current) return;
      console.error('Failed to load duplicate candidates:', candidateError);
      setLoadError('Unable to load duplicate candidates.');
    } finally {
      if (mountedRef.current && requestId === candidateRequestRef.current) setLoading(false);
    }
  }, [task.leadId]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const clearCandidate = async (candidate) => {
    setClearingId(candidate.id);
    try {
      await resolveIntakeDuplicateCandidate(task.leadId, candidate.id, {
        status: 'not_duplicate',
        reason: `Cleared duplicate candidate lead #${candidate.candidate_lead_id} from Inbox`,
      });
      showToast({
        severity: 'success',
        summary: 'Not Duplicate',
        detail: 'The duplicate concern was cleared.',
      });
      await loadCandidates();
      await onTaskChanged(task.id);
    } catch (candidateError) {
      console.error('Failed to clear duplicate candidate:', candidateError);
      showToast({
        severity: 'error',
        summary: 'Resolution failed',
        detail: candidateError?.response?.data?.error || 'Failed to clear the duplicate concern.',
      });
    } finally {
      if (mountedRef.current) setClearingId(null);
    }
  };

  const openMerge = (candidate) => {
    const label = duplicateCandidateLabel(candidate.candidate_lead);
    setMergeCandidate(candidate);
    setMergeReason(`Duplicate of ${label}`);
    setMergeNotes('');
  };

  const confirmMerge = async () => {
    if (!mergeCandidate || !mergeReason.trim()) return;
    setMerging(true);
    try {
      await mergeIntakeDuplicateCandidate(task.leadId, mergeCandidate.id, {
        reason: mergeReason.trim(),
        notes: mergeNotes.trim(),
      });
      showToast({
        severity: 'success',
        summary: 'Merged',
        detail: 'The duplicate lead was merged and the concern was cleared.',
      });
      setMergeCandidate(null);
      await loadCandidates();
      await onTaskChanged(task.id);
    } catch (candidateError) {
      console.error('Failed to merge duplicate candidate:', candidateError);
      showToast({
        severity: 'error',
        summary: 'Merge failed',
        detail: candidateError?.response?.data?.error || 'Failed to merge the duplicate lead.',
      });
    } finally {
      if (mountedRef.current) setMerging(false);
    }
  };

  const mergeLabel = duplicateCandidateLabel(mergeCandidate?.candidate_lead);

  return (
    <>
      <section className="task-inbox__duplicate-review" aria-labelledby="inbox-duplicate-review-heading">
        <div className="task-inbox__section-heading">
          <div>
            <h3 id="inbox-duplicate-review-heading">Resolve duplicate concern</h3>
            <p>{candidates.length} unresolved match{candidates.length === 1 ? '' : 'es'}</p>
          </div>
        </div>
        {loading ? (
          <div className="task-inbox__duplicate-state">Loading candidate matches…</div>
        ) : loadError ? (
          <div className="task-inbox__duplicate-state" role="alert">
            <span>{loadError}</span>
            <button type="button" className="task-inbox__button" onClick={loadCandidates}>Try again</button>
          </div>
        ) : candidates.length ? (
          <div className="task-inbox__duplicate-list">
            {candidates.map((candidate) => {
              return (
                <article className="task-inbox__duplicate-candidate" key={candidate.id}>
                  <div className="task-inbox__duplicate-candidate-heading">
                    <span>{humanizeKey(candidate.status)}</span>
                    <strong>Match score {candidate.score}</strong>
                  </div>
                  <DuplicateLeadComparison task={task} candidate={candidate} />
                  <div className="task-inbox__duplicate-actions">
                    <button
                      type="button"
                      className="task-inbox__button"
                      disabled={clearingId === candidate.id || merging}
                      onClick={() => clearCandidate(candidate)}
                    >
                      <AppIcon icon="pi pi-check" aria-hidden="true" />
                      {clearingId === candidate.id ? 'Clearing…' : 'Not Duplicate'}
                    </button>
                    <button
                      type="button"
                      className="task-inbox__button task-inbox__button--primary"
                      disabled={Boolean(clearingId) || merging}
                      onClick={() => openMerge(candidate)}
                    >
                      <AppIcon icon="pi pi-sitemap" aria-hidden="true" />
                      Merge
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="task-inbox__duplicate-state">
            <AppIcon icon="pi pi-check-circle" aria-hidden="true" />
            Duplicate concern resolved.
          </div>
        )}
      </section>

      <AppModal
        visible={Boolean(mergeCandidate)}
        title="Merge Duplicate Lead"
        description={`Merge details from ${mergeLabel} and clear this duplicate concern.`}
        onClose={() => setMergeCandidate(null)}
        footer={(
          <button
            type="button"
            className="task-inbox__button task-inbox__button--primary"
            onClick={confirmMerge}
            disabled={merging || !mergeReason.trim()}
          >
            <AppIcon icon="pi pi-sitemap" aria-hidden="true" />
            {merging ? 'Merging…' : 'Merge Lead'}
          </button>
        )}
        width="min(920px, calc(100vw - 2rem))"
      >
        <div className="task-inbox__merge-form">
          {mergeCandidate ? <DuplicateLeadComparison task={task} candidate={mergeCandidate} /> : null}
          <label>
            <span>Reason</span>
            <input value={mergeReason} onChange={(event) => setMergeReason(event.target.value)} />
          </label>
          <label>
            <span>Notes</span>
            <textarea rows={4} value={mergeNotes} onChange={(event) => setMergeNotes(event.target.value)} />
          </label>
        </div>
      </AppModal>
    </>
  );
}

function readStoredInboxViewState() {
  if (typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(INBOX_VIEW_STATE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredInboxViewState(state) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(INBOX_VIEW_STATE_KEY, JSON.stringify(state));
  } catch {
    // The Inbox remains usable if session storage is not available.
  }
}

export default function TaskInbox() {
  const showToast = useContext(ToastContext);
  const [urlParams] = useSearchParams();
  const [storedViewState] = useState(() => {
    const stored = readStoredInboxViewState();
    if (!urlParams.has('task')) return stored;
    return { ...stored, familyFilter: 'payment_instructions', typeFilter: 'all', search: '', assigneeFilter: null,
      viewFilter: urlParams.get('view') === 'completed' ? 'completed' : 'all',
      statusFilter: urlParams.get('view') === 'completed' ? 'completed' : 'open',
      selectedTaskId: Number(urlParams.get('task')) || null };
  });
  const listRef = useRef(null);
  const detailRef = useRef(null);
  const detailScrollByTaskRef = useRef(storedViewState.detailScrollByTask || {});
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ all: 0, mine: 0, unassigned: 0, completed: 0 });
  const [actor, setActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState(storedViewState.selectedTaskId || null);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const resolvedInitialQueryRef = useRef(null);
  const [familyFilter, setFamilyFilter] = useState(storedViewState.familyFilter || TASK_FAMILIES[0].key);
  const [typeFilter, setTypeFilter] = useState(storedViewState.typeFilter || 'all');
  const [viewFilter, setViewFilter] = useState(['mine', 'unassigned'].includes(storedViewState.viewFilter) ? storedViewState.viewFilter : 'all');
  const [assigneeFilter, setAssigneeFilter] = useState(['mine', 'unassigned'].includes(storedViewState.viewFilter) ? null : storedViewState.assigneeFilter || null);
  const [assignees, setAssignees] = useState([]);
  const [statusFilter, setStatusFilter] = useState(storedViewState.viewFilter === 'completed' ? 'completed' : storedViewState.statusFilter || 'open');
  const [search, setSearch] = useState(storedViewState.search || '');
  const [debouncedSearch, setDebouncedSearch] = useState(storedViewState.search || '');
  const [pagination, setPagination] = useState({ page: storedViewState.page || 1, page_size: 50, total: 0, pages: 1 });
  const pageRef = useRef(storedViewState.page || 1);
  const [firstPage, setFirstPage] = useState(storedViewState.page || 1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(null);
  const earlierRef = useRef(null);
  const moreRef = useRef(null);
  const fetchingRef = useRef(false);
  const scrollAnchorRef = useRef(null);
  const focusTaskRef = useRef(storedViewState.selectedTaskId || null);
  const queryKey = JSON.stringify([familyFilter, typeFilter, viewFilter, statusFilter, debouncedSearch, assigneeFilter?.key]);
  const queryKeyRef = useRef(queryKey);
  const activeQueryKeyRef = useRef(queryKey);
  activeQueryKeyRef.current = queryKey;
  const [loadedQueryKey, setLoadedQueryKey] = useState(null);
  const hasCurrentQuery = loadedQueryKey === queryKey;
  const activeTaskIdRef = useRef(null);
  const displayedTypeRef = useRef(typeFilter);
  const [activityLoading, setActivityLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const listRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const listAbortRef = useRef(null);
  const detailAbortRef = useRef(null);

  const loadTasks = useCallback(async (overrides = {}) => {
    // A completed task action or disconnected scroll observer may retain an old query.
    if (queryKey !== activeQueryKeyRef.current) return null;
    const extending = Boolean(overrides.direction);
    if (extending && fetchingRef.current) return null;
    fetchingRef.current = true;
    if (queryKeyRef.current !== queryKey) {
      pageRef.current = 1;
      focusTaskRef.current = null;
      queryKeyRef.current = queryKey;
    }
    const requestId = ++listRequestRef.current;
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    const nextFamily = overrides.family || familyFilter;
    const nextType = overrides.type || typeFilter;
    const nextView = overrides.view || viewFilter;
    if (extending) setLoadingMore(true);
    else { setLoading(true); setLoadingMore(false); }
    setMoreError(null);
    setError('');
    try {
      const response = await api.get('/tasks', {
        signal: controller.signal,
        params: {
          single_type: 1,
          family: nextFamily,
          type: nextType,
          view: nextView,
          status: statusFilter,
          page: overrides.page || pageRef.current,
          page_size: 50,
          ...(assigneeFilter ? { assignee: assigneeFilter.key } : {}),
          search: debouncedSearch.trim(),
          ...(focusTaskRef.current ? { focus_task_id: focusTaskRef.current } : {}),
        },
      });
      if (requestId !== listRequestRef.current || queryKey !== activeQueryKeyRef.current) return null;
      const nextTasks = (response.data?.tasks || []).map((task) => ({
        ...toInboxTask(task), inboxPage: response.data?.pagination?.page || 1,
      }));
      if (extending) {
        const list = listRef.current;
        const top = list?.getBoundingClientRect().top;
        const anchor = list && Array.from(list.querySelectorAll('[data-task-id]')).find((row) => row.getBoundingClientRect().bottom > top);
        scrollAnchorRef.current = anchor ? { id: anchor.dataset.taskId, top: anchor.getBoundingClientRect().top } : null;
        setTasks((current) => Array.from(new Map(
          (overrides.direction === 'earlier' ? [...nextTasks, ...current] : [...current, ...nextTasks])
            .map((task) => [task.id, task]),
        ).values()));
      } else setTasks(nextTasks);
      setAssignees(response.data?.assignees || []);
      setCounts(response.data?.counts || {});
      setActor(response.data?.current_actor || null);
      setHasLoaded(true);
      const nextPagination = response.data?.pagination || { page: 1, page_size: 50, total: nextTasks.length, pages: 1 };
      if (!extending || overrides.direction === 'earlier') {
        pageRef.current = nextPagination.page;
        setFirstPage(nextPagination.page);
      }
      focusTaskRef.current = null;
      setPagination((current) => overrides.direction === 'earlier' ? { ...nextPagination, page: current.page } : nextPagination);
      if (displayedTypeRef.current !== nextType && listRef.current) listRef.current.scrollTop = 0;
      displayedTypeRef.current = nextType;
      if (!extending) setSelectedTaskId((current) => (
        nextTasks.some((task) => task.id === current) ? current : nextTasks[0]?.id || null
      ));
      // Resolve the initial/deep-linked type in the same response as its first page.
      const resolvedType = response.data?.filters?.type || nextTasks[0]?.typeKey || availableDefaultType(nextFamily);
      if (nextType === 'all') {
        const resolvedKey = JSON.stringify([familyFilter, resolvedType, viewFilter, statusFilter, debouncedSearch, assigneeFilter?.key]);
        resolvedInitialQueryRef.current = resolvedKey;
        queryKeyRef.current = resolvedKey;
        activeQueryKeyRef.current = resolvedKey;
        displayedTypeRef.current = resolvedType;
        fetchingRef.current = false;
        setLoading(false);
        setTypeFilter(resolvedType);
        setLoadedQueryKey(resolvedKey);
      } else setLoadedQueryKey(queryKey);
      return nextTasks;
    } catch (loadError) {
      if (requestId !== listRequestRef.current || controller.signal.aborted || queryKey !== activeQueryKeyRef.current) return null;
      if (extending) {
        setMoreError({ page: overrides.page, direction: overrides.direction });
        return null;
      }
      console.error('Failed to load task inbox:', loadError);
      setError('Unable to load your inbox.');
      showToast({
        severity: 'error',
        summary: 'Inbox unavailable',
        detail: 'Please try loading your tasks again.',
      });
      return [];
    } finally {
      if (requestId === listRequestRef.current) {
        fetchingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [assigneeFilter, debouncedSearch, familyFilter, queryKey, showToast, statusFilter, typeFilter, viewFilter]);

  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    scrollAnchorRef.current = null;
    if (!anchor || !listRef.current) return;
    const row = listRef.current.querySelector(`[data-task-id="${anchor.id}"]`);
    if (row) listRef.current.scrollTop += row.getBoundingClientRect().top - anchor.top;
  }, [tasks]);

  useEffect(() => {
    if (!hasCurrentQuery || !hasLoaded || loading || loadingMore || moreError || search !== debouncedSearch || !window.IntersectionObserver) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries.find((item) => item.isIntersecting);
      if (!entry) return;
      const earlier = entry.target === earlierRef.current;
      loadTasks({ page: earlier ? firstPage - 1 : pagination.page + 1, direction: earlier ? 'earlier' : 'later' });
    }, { root: listRef.current, rootMargin: '200px 0px' });
    if (earlierRef.current) observer.observe(earlierRef.current);
    if (moreRef.current) observer.observe(moreRef.current);
    return () => observer.disconnect();
  }, [debouncedSearch, firstPage, hasCurrentQuery, hasLoaded, loadTasks, loading, loadingMore, moreError, pagination.page, pagination.pages, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadTaskDetail = useCallback(async (taskId) => {
    const requestId = ++detailRequestRef.current;
    detailAbortRef.current?.abort();
    const controller = new AbortController();
    detailAbortRef.current = controller;
    setActivityLoading(true);
    setDetailError('');
    try {
      const response = await api.get(`/tasks/${taskId}`, { signal: controller.signal });
      if (requestId === detailRequestRef.current) setSelectedTaskDetail(toInboxTask(response.data));
    } catch {
      if (requestId === detailRequestRef.current && !controller.signal.aborted) {
        setSelectedTaskDetail(null);
        setDetailError('Unable to load this task.');
      }
    } finally {
      if (requestId === detailRequestRef.current) setActivityLoading(false);
    }
  }, []);

  const handleTaskChanged = useCallback(async (taskId) => {
    if (queryKey !== activeQueryKeyRef.current) return;
    focusTaskRef.current = taskId;
    const nextTasks = await loadTasks();
    if (activeTaskIdRef.current === taskId && nextTasks?.some((task) => task.id === taskId)) await loadTaskDetail(taskId);
  }, [loadTasks, loadTaskDetail, queryKey]);

  useEffect(() => {
    if (resolvedInitialQueryRef.current === queryKey) resolvedInitialQueryRef.current = null;
    else loadTasks();
    return () => { listRequestRef.current += 1; listAbortRef.current?.abort(); };
  }, [loadTasks, queryKey]);

  const persistInboxViewState = useCallback(() => {
    writeStoredInboxViewState({
      selectedTaskId,
      familyFilter,
      typeFilter,
      viewFilter,
      assigneeFilter,
      statusFilter,
      search,
      page: firstPage,
      detailScrollByTask: detailScrollByTaskRef.current,
    });
  }, [assigneeFilter, familyFilter, firstPage, search, selectedTaskId, statusFilter, typeFilter, viewFilter]);

  useEffect(() => {
    persistInboxViewState();
  }, [persistInboxViewState]);

  // Never label rows or details from a previous query as the newly selected type.
  const visibleTasks = useMemo(() => hasCurrentQuery ? tasks.filter((task) => task.typeKey === typeFilter) : [], [hasCurrentQuery, tasks, typeFilter]);
  const selectedTask = visibleTasks.find((task) => task.id === selectedTaskId) || null;
  activeTaskIdRef.current = selectedTask?.id || null;
  const detailTask = selectedTask && selectedTaskDetail?.id === selectedTaskId ? selectedTaskDetail : selectedTask;

  useEffect(() => {
    const scrollTop = Number(detailScrollByTaskRef.current[selectedTaskId]) || 0;
    const frame = window.requestAnimationFrame(() => {
      if (detailRef.current) detailRef.current.scrollTop = scrollTop;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [detailTask?.id, selectedTaskId]);

  const handleDetailScroll = useCallback((event) => {
    if (!selectedTaskId) return;
    detailScrollByTaskRef.current[selectedTaskId] = event.currentTarget.scrollTop;
    persistInboxViewState();
  }, [persistInboxViewState, selectedTaskId]);

  useEffect(() => {
    if (!selectedTask?.id) {
      setSelectedTaskDetail(null);
      setDetailError('');
      setActivityLoading(false);
      return undefined;
    }
    loadTaskDetail(selectedTask.id);
    return () => { detailRequestRef.current += 1; detailAbortRef.current?.abort(); };
  }, [selectedTask?.id, loadTaskDetail]);

  const groupedTasks = useMemo(() => {
    const knownTypeKeys = new Set(TASK_TYPES.map((type) => type.key));
    const fallbackTypes = [];
    const fallbackTypeKeys = new Set();
    visibleTasks.forEach((task) => {
      const key = task.typeKey || 'other';
      if (knownTypeKeys.has(key) || fallbackTypeKeys.has(key)) return;
      fallbackTypeKeys.add(key);
      fallbackTypes.push({
        key,
        label: task.verb || 'Other tasks',
        icon: task.icon || 'pi pi-check-square',
      });
    });
    return [...TASK_TYPES, ...fallbackTypes].filter((type) => type.key === typeFilter).map((type) => ({
      ...type, sectionKey: type.key, tasks: visibleTasks,
    }));
  }, [typeFilter, visibleTasks]);
  const availableTaskTypes = TASK_TYPES.filter((type) => familyFilter === 'all' || (type.family || 'intake_document_coordination') === familyFilter);
  if (typeFilter !== 'all' && !availableTaskTypes.some((type) => type.key === typeFilter)) {
    availableTaskTypes.push({ key: typeFilter, label: humanizeKey(typeFilter), icon: 'pi pi-check-square' });
  }
  const selectedTypeLabel = availableTaskTypes.find((type) => type.key === typeFilter)?.label || 'Tasks';

  const taskActivities = (detailTask?.activities || []).map((activity) => ({
    id: `task:${activity.id}`,
    title: taskActivityTitle(activity.action_type),
    summary: activity.summary,
    source_label: 'Task history',
    status: activity.to_status || null,
    actor: activity.actor?.name || activity.actor?.email || null,
    timestamp: activity.created_at,
  }));
  const visibleActivity = taskActivities.slice(0, 35);

  const updateView = (value) => {
    setViewFilter(value);
    setAssigneeFilter(null);
  };

  return (
    <div className="task-inbox">
      <section className="task-inbox__shell" aria-label="Task inbox workspace">
        <nav className="task-inbox__ownership" aria-label="Task ownership">
          {OWNERSHIP_VIEWS.map((view) => <button key={view.key} type="button"
            className={`task-inbox__ownership-tab${viewFilter === view.key && !assigneeFilter ? ' task-inbox__ownership-tab--active' : ''}`}
            aria-pressed={viewFilter === view.key && !assigneeFilter} onClick={() => updateView(view.key)}>
            <AppIcon icon={view.icon} aria-hidden="true" />
            {view.label}<span>{!hasCurrentQuery ? '—' : counts[view.key] || 0}</span>
          </button>)}
        </nav>
        <section className="task-inbox__filters" aria-label="Task inbox filters">
          <label>
            <span>Assignee</span>
            <select value={assigneeFilter?.key || ''} onChange={(event) => {
              setAssigneeFilter(assignees.find((person) => person.key === event.target.value) || null);
              setViewFilter('all');
            }}>
              <option value="">{viewFilter === 'mine' ? 'Me' : viewFilter === 'unassigned' ? 'Unassigned' : 'All assignees'}</option>
              {assigneeFilter && !assignees.some((person) => person.key === assigneeFilter.key)
                ? <option value={assigneeFilter.key}>{assigneeFilter.name}</option> : null}
              {assignees.map((person) => <option key={person.key} value={person.key}>{person.name}</option>)}
            </select>
          </label>
          <label>
            <span>Task family</span>
            <select value={familyFilter} onChange={(event) => { setFamilyFilter(event.target.value); setTypeFilter('all'); }}>
              {TASK_FAMILIES.map((family) => (
                <option key={family.key} value={family.key}>{family.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Status</span>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="open">Open</option>
              <option value="completed">Completed</option>
            </select>
          </label>
          <label className="task-inbox__search">
            <span>Search</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Task, client, matter, defendant"
            />
          </label>
        </section>

        {error ? (
          <section className="task-inbox__empty" role="alert">
            <AppIcon icon="pi pi-exclamation-circle" aria-hidden="true" />
            <strong>{error}</strong>
            <button type="button" className="task-inbox__button" onClick={() => loadTasks()}>Try again</button>
          </section>
        ) : (
          <div className="task-inbox__workspace">
            <nav className="task-inbox__type-nav" aria-label="Task type selector">
              {availableTaskTypes.map((type) => (
                <button
                  key={type.key}
                  type="button"
                  className={`task-inbox__type-option${typeFilter === type.key ? ' task-inbox__type-option--active' : ''}`}
                  onClick={() => setTypeFilter(type.key)}
                  aria-label={`Show ${type.label}`}
                  aria-pressed={typeFilter === type.key}
                >
                  <AppIcon icon={type.icon} aria-hidden="true" />
                  <span className="task-inbox__type-label" aria-hidden="true">{type.label}</span>
                </button>
              ))}
            </nav>
          <section className="task-inbox__list" ref={listRef} aria-label="Firm tasks" aria-busy={loading}>
            <div className="task-inbox__section-heading">
              <div>
                <h2>{selectedTypeLabel}</h2>
                <p role="status">{!hasCurrentQuery || loading || search !== debouncedSearch ? (hasLoaded ? 'Updating tasks…' : 'Loading your inbox…') : `${visibleTasks.length} of ${pagination.total} task${pagination.total === 1 ? '' : 's'}`}</p>
              </div>
            </div>

            {hasCurrentQuery && firstPage > 1 ? <div ref={earlierRef} className="task-inbox__scroll-status" aria-label="Load earlier tasks">{loadingMore ? 'Loading more tasks…' : 'Scroll up for earlier tasks'}</div> : null}
            {!hasCurrentQuery ? (
              <InboxLoading />
            ) : visibleTasks.length ? (
              <div className="task-inbox__task-groups">
                {groupedTasks.map((group) => (
                  <section
                    className="task-inbox__task-group"
                    key={group.sectionKey}
                    aria-labelledby={`task-group-${group.sectionKey}`}
                  >
                    <header className="task-inbox__task-group-header">
                      <span className="task-inbox__task-group-icon"><AppIcon icon={group.icon} aria-hidden="true" /></span>
                      <h3 id={`task-group-${group.sectionKey}`}>{group.label}</h3>
                      <span>{group.tasks.length} task{group.tasks.length === 1 ? '' : 's'}</span>
                    </header>
                    <ol className="task-inbox__task-list">
                      {group.tasks.map((task) => (
                        <li key={task.id} data-task-id={task.id}>
                          <article className={`task-inbox__task${selectedTaskId === task.id ? ' task-inbox__task--selected' : ''}`}>
                            <button type="button" onClick={() => setSelectedTaskId(task.id)} aria-pressed={selectedTaskId === task.id}>
                              <span className="task-inbox__task-icon"><AppIcon icon={task.icon} aria-hidden="true" /></span>
                              <span className="task-inbox__task-body">
                                <strong>{task.subjectName}</strong>
                                <span>{task.paymentReview ? `Matter ${task.paymentReview.matter.number}` : <InboxValue value={task.defendant || task.company} fallback="Defendant not recorded" />}</span>
                                {task.paymentReview ? <span className="task-inbox__task-signals"><small>{task.status === 'completed' ? 'Completed' : task.paymentReview.status === 'held' ? 'Waiting' : 'Open'}</small>{task.paymentReview.test_mode ? <small>Sandbox</small> : null}</span> : null}
                                <span className="task-inbox__task-signals">
                                  {[task.claimType, task.state, humanizeKey(task.stage)].filter(Boolean).map((value) => (
                                    <small key={value}>{humanizeKey(value)}</small>
                                  ))}
                                </span>
                                <span className="task-inbox__row-owner"><AppIcon icon="pi pi-user" aria-hidden="true" />{ownerLabel(task.taskOwner)}</span>
                                <small>{task.reason}</small>
                              </span>
                              {task.dueAt || task.batch?.number ? (
                                <span className="task-inbox__task-meta">
                                  {task.dueAt ? (
                                    <span className={`task-inbox__urgency task-inbox__urgency--${task.urgency.key}`}>
                                      <AppIcon icon={urgencyIcon(task.urgency.key)} aria-hidden="true" />
                                      {task.urgency.label}
                                    </span>
                                  ) : null}
                                  {task.batch?.number ? <span>Batch #{task.batch.number}</span> : null}
                                </span>
                              ) : null}
                            </button>
                          </article>
                        </li>
                      ))}
                    </ol>
                  </section>
                ))}
              </div>
            ) : <div className="task-inbox__empty">No tasks match these filters.</div>}
            {moreError ? <div className="task-inbox__scroll-status" role="alert">
              Could not load more tasks. <button type="button" className="task-inbox__button" onClick={() => loadTasks(moreError)}>Retry loading tasks</button>
            </div> : null}
            {hasCurrentQuery && pagination.page < pagination.pages ? (
              <div ref={moreRef} className="task-inbox__scroll-status" role="status" aria-label="Load more tasks">
                {loadingMore ? 'Loading more tasks…' : 'Scroll for more tasks'}
              </div>
            ) : hasCurrentQuery && tasks.length ? <div className="task-inbox__scroll-status">End of tasks</div> : null}
          </section>

          <aside className="task-inbox__detail" aria-label="Selected task" ref={detailRef} onScroll={handleDetailScroll} aria-busy={loading || activityLoading}>
            {!hasCurrentQuery || (selectedTask && selectedTaskDetail?.id !== selectedTask.id && !detailError) ? (
              <InboxLoading detail />
            ) : detailError ? (
              <div className="task-inbox__empty" role="alert">
                <strong>{detailError}</strong>
                <button type="button" className="task-inbox__button" onClick={() => loadTaskDetail(selectedTaskId)}>Try again</button>
              </div>
            ) : detailTask ? (
              <fieldset className="task-inbox__detail-content" disabled={loading || activityLoading}>
                {activityLoading ? <div className="task-inbox__refresh-status" role="status">Updating task…</div> : null}
                <header className="task-inbox__detail-header">
                  <span>{detailTask.sourceLabel || detailTask.familyLabel}</span>
                  <h2>{detailTask.verb}</h2>
                  <p>{detailTask.reason}</p>
                  <div className="task-inbox__detail-signals" aria-label="Task timing">
                    {detailTask.dueAt ? (
                      <span className={`task-inbox__urgency task-inbox__urgency--${detailTask.urgency.key}`}>
                        <AppIcon icon={urgencyIcon(detailTask.urgency.key)} aria-hidden="true" />
                        {detailTask.urgency.label}
                      </span>
                    ) : null}
                    <span>{detailTask.ageLabel}</span>
                  </div>
                  <div className="task-inbox__detail-links">
                    <Link to={`${detailTask.subjectHref}?from=inbox`} className="task-inbox__lead-link" aria-label={`Open ${detailTask.paymentReview ? "matter" : "lead"} details for ${detailTask.subjectName}`}>
                      <AppIcon icon="pi pi-external-link" aria-hidden="true" />
                      {detailTask.subjectName}
                    </Link>
                    {detailTask.leadId ? (
                      <Link className="task-inbox__lead-link" to={`/intake/leads/${detailTask.leadId}?from=inbox`} target="_blank" rel="noopener noreferrer" title="Open intake details in a new tab">
                        <AppIcon icon="pi pi-external-link" aria-hidden="true" /> Open intake details
                      </Link>
                    ) : null}
                    {getPipedriveRecordUrl(detailTask.leadSource) ? (
                      <a className="task-inbox__lead-link" href={getPipedriveRecordUrl(detailTask.leadSource)} target="_blank" rel="noopener noreferrer">
                        <AppIcon icon="pi pi-external-link" aria-hidden="true" /> Open in Pipedrive
                      </a>
                    ) : null}
                  </div>
                </header>

                <TaskAssignment key={detailTask.id} task={detailTask} actor={actor} onTaskChanged={handleTaskChanged} />
                {detailTask.typeKey === 'verify_payment_banking' && detailTask.paymentReview ? (
                  <PortalResult embedded reviewId={detailTask.paymentReview.id}
                    matterNumber={detailTask.paymentReview.matter.number} clientId={detailTask.paymentReview.client_id}
                    onChanged={() => handleTaskChanged(detailTask.id)} />
                ) : detailTask.paymentReview ? (
                  <PaymentReviewPanel assignmentManaged canReview={!!actor && (detailTask.taskOwner?.employee_id
                    ? detailTask.taskOwner.employee_id === actor.employee_id
                    : !!actor.email && detailTask.taskOwner?.email?.toLowerCase() === actor.email.toLowerCase())} key={`${detailTask.id}:${detailTask.paymentReview.revision}`} task={detailTask} onTaskChanged={handleTaskChanged} />
                ) : <>
                {detailTask.typeKey === 'review_duplicate' ? (
                  <InboxDuplicateResolution task={detailTask} onTaskChanged={handleTaskChanged} />
                ) : null}

                <section className="task-inbox__context-section" aria-labelledby="selected-lead-context">
                  <div className="task-inbox__section-heading">
                    <div>
                      <h3 id="selected-lead-context">Lead orientation</h3>
                      <p><InboxValue value={[detailTask.defendant, detailTask.company].filter((value, index, values) => value && values.indexOf(value) === index).join(' · ')} fallback="Defendant or company not recorded" /></p>
                    </div>
                    {detailTask.batch?.href ? (
                      <Link to={detailTask.batch.href} className="task-inbox__compact-link">
                        Batch #{detailTask.batch.number} <AppIcon icon="pi pi-arrow-right" aria-hidden="true" />
                      </Link>
                    ) : null}
                  </div>
                  <dl className="task-inbox__facts">
                    <div><dt>Defendant</dt><dd><InboxValue value={detailTask.defendant} /></dd></div>
                    <div><dt>Company</dt><dd><InboxValue value={detailTask.company} /></dd></div>
                    <div><dt>Claim type</dt><dd><InboxValue value={humanizeKey(detailTask.claimType)} /></dd></div>
                    <div><dt>State</dt><dd><InboxValue value={detailTask.state} /></dd></div>
                    <div><dt>Jurisdiction</dt><dd><InboxValue value={detailTask.jurisdiction} /></dd></div>
                    <div><dt>Intake stage</dt><dd><InboxValue value={humanizeKey(detailTask.stage)} /></dd></div>
                    <div><dt>Disposition</dt><dd><InboxValue value={humanizeKey(detailTask.disposition)} /></dd></div>
                    <div><dt>Lead owner</dt><dd>{ownerLabel(detailTask.leadOwner)}</dd></div>
                    <div><dt>Lead source</dt><dd><InboxValue value={leadSourceLabel(detailTask.leadSource)} /></dd></div>
                    <div>
                      <dt>Batch position</dt>
                      <dd>{detailTask.batch ? [humanizeKey(detailTask.batch.status), humanizeKey(detailTask.batch.member_status), detailTask.batch.rank ? `Rank ${detailTask.batch.rank}` : null, detailTask.batch.score != null ? `Score ${detailTask.batch.score}` : null].filter(Boolean).join(' · ') : 'Not in a batch'}</dd>
                    </div>
                  </dl>
                  {detailTask.convertedMatter ? (
                    <div className="task-inbox__optional-matter">
                      <span>Converted matter</span>
                      <Link to={detailTask.convertedMatter.href}>{detailTask.convertedMatter.number}</Link>
                    </div>
                  ) : null}
                </section>

                <section className="task-inbox__context-section" aria-labelledby="selected-task-brief">
                  <div className="task-inbox__section-heading">
                    <div><h3 id="selected-task-brief">Task brief</h3><p>What needs attention and why.</p></div>
                  </div>
                  <dl className="task-inbox__brief">
                    <div><dt>Action needed</dt><dd>{detailTask.actionNeeded || 'Open the lead and review the current Intake state.'}</dd></div>
                    <div><dt>Why this exists</dt><dd>{detailTask.reason || 'No reason was recorded.'}</dd></div>
                    <div><dt>Task source</dt><dd><InboxValue value={detailTask.sourceLabel} /></dd></div>
                    <div><dt>Task owner</dt><dd>{ownerLabel(detailTask.taskOwner)}</dd></div>
                    <div><dt>Age</dt><dd>{detailTask.ageLabel}</dd></div>
                    {detailTask.dueAt ? <div><dt>Due</dt><dd>{formatDate(detailTask.dueAt)}</dd></div> : null}
                  </dl>
                </section>

                <section className="task-inbox__context-section" aria-labelledby="latest-lead-activity">
                  <div className="task-inbox__section-heading">
                    <div><h3 id="latest-lead-activity">Latest contact or activity</h3></div>
                  </div>
                  {detailTask.latestActivity ? (
                    <div className="task-inbox__latest-activity">
                      <span className="task-inbox__activity-marker" aria-hidden="true" />
                      <div>
                        <strong>{humanizeKey(detailTask.latestActivity.label)}</strong>
                        <p>{detailTask.latestActivity.summary}</p>
                        <small>{[detailTask.latestActivity.actor, detailTask.latestActivity.occurred_at ? formatDate(detailTask.latestActivity.occurred_at) : null].filter(Boolean).join(' · ')}</small>
                      </div>
                    </div>
                  ) : <p className="task-inbox__context-empty">No contact or activity is recorded for this lead.</p>}
                </section>

                <section className="task-inbox__context-section" aria-labelledby="lead-document-context">
                  <div className="task-inbox__section-heading">
                    <div>
                      <h3 id="lead-document-context">Documents and outstanding information</h3>
                      <p>{detailTask.documents.receivedCount} received · {detailTask.documents.pendingReviewCount} pending review</p>
                    </div>
                  </div>
                  <div className="task-inbox__document-context">
                    <div>
                      <h4>Received</h4>
                      {detailTask.documents.received.length ? (
                        <ul>{detailTask.documents.received.map((document) => <li key={document.id}>{document.title}<small>{humanizeKey(document.review_status) || 'Status not recorded'}</small></li>)}</ul>
                      ) : <p>None recorded</p>}
                    </div>
                    <div>
                      <h4>Outstanding</h4>
                      {detailTask.documents.outstandingInformation.length ? (
                        <ul>{detailTask.documents.outstandingInformation.map((item) => <li key={item}>{item}</li>)}</ul>
                      ) : <p>None recorded</p>}
                    </div>
                  </div>
                </section>

                {OUTREACH_WORKSPACE_TYPES.has(detailTask.typeKey) ? (
                  <OutreachWorkspace
                    lead={{
                      id: detailTask.leadId,
                      prospect_name: detailTask.leadName,
                      prospect_email: detailTask.prospectEmail,
                      prospect_phone: detailTask.prospectPhone,
                    }}
                    onRefreshLead={loadTasks}
                  />
                ) : null}

                </>}

                <section className="task-inbox__activity">
                  <div className="task-inbox__section-heading">
                    <div><h3>Task history</h3><p>Immutable changes to this operational task.</p></div>
                  </div>
                  {activityLoading && !visibleActivity.length ? (
                    <div className="task-inbox__empty">Loading activity…</div>
                  ) : visibleActivity.length ? (
                    <ol>
                      {visibleActivity.map((item) => (
                        <li key={item.id}>
                          <span className="task-inbox__activity-marker" aria-hidden="true" />
                          <div>
                            <strong>{item.title}</strong>
                            <p>{item.summary || 'Activity recorded.'}</p>
                            <small>
                              {[item.source_label, item.status, item.actor].filter(Boolean).join(' · ')}
                              {(item.timestamp || item.due_timestamp) ? ` · ${formatDate(item.timestamp || item.due_timestamp)}` : ''}
                            </small>
                          </div>
                        </li>
                      ))}
                    </ol>
                  ) : <div className="task-inbox__empty">No activity is recorded for this task yet.</div>}
                </section>
              </fieldset>
            ) : <div className="task-inbox__empty">Select a task to see its context and activity.</div>}
          </aside>
          </div>
        )}
      </section>
    </div>
  );
}
